import Papa from "papaparse";

import type { VariableDataType } from "@/components/rule-canvas-builder/hooks/useRuleVariables";

import { createDraftCollectionEntry, type CollectionEntry } from "./collectionEntryUtils";

export interface CsvImportLineError {
	line: number;
	value: string;
	message: string;
}

export type CsvImportResult =
	| { ok: true; entries: CollectionEntry[] }
	| { ok: false; errors: CsvImportLineError[] };

function stripBom(text: string): string {
	return text.replace(/^\uFEFF/, "");
}

/** One value per line when the file has no delimiter characters (common for single-column exports). */
function parseFirstColumnAsLines(csvText: string): { line: number; value: string }[] {
	const results: { line: number; value: string }[] = [];
	const lines = csvText.split(/\r\n|\n|\r/);
	for (let i = 0; i < lines.length; i++) {
		const value = lines[i].trim();
		if (!value) continue;
		results.push({ line: i + 1, value });
	}
	return results;
}

function isDelimiterDetectionWarning(error: Papa.ParseError): boolean {
	return (
		error.type === "Delimiter"
		|| /auto-detect/i.test(error.message)
		|| /defaulted to/i.test(error.message)
	);
}

/** Parse the first column of each non-empty CSV row via Papa Parse. */
export function parseCsvFirstColumnValues(
	csvText: string,
): { line: number; value: string }[] | { error: string; line?: number } {
	const normalized = stripBom(csvText);

	if (!/[,;\t|]/.test(normalized)) {
		return parseFirstColumnAsLines(normalized);
	}

	const { data, errors } = Papa.parse<string[]>(normalized, {
		delimiter: ",",
		skipEmptyLines: true,
	});

	const blockingErrors = errors.filter((err) => !isDelimiterDetectionWarning(err));
	if (blockingErrors.length > 0) {
		const first = blockingErrors[0];
		return {
			error: first.message || "Invalid CSV format",
			line: typeof first.row === "number" ? first.row + 1 : undefined,
		};
	}

	const results: { line: number; value: string }[] = [];

	data.forEach((row, index) => {
		const lineNumber = index + 1;
		const value = String(row[0] ?? "").trim();
		if (!value) return;
		results.push({ line: lineNumber, value });
	});

	return results;
}

export function validateEntryValueForDataType(
	raw: string,
	dataType: VariableDataType,
): { ok: true; normalized: string } | { ok: false; message: string } {
	const trimmed = raw.trim();
	if (!trimmed) {
		return { ok: false, message: "Empty value" };
	}

	switch (dataType) {
		case "number": {
			if (!/^-?\d+$/.test(trimmed)) {
				return { ok: false, message: "Expected an integer" };
			}
			return { ok: true, normalized: String(Number(trimmed)) };
		}
		case "date": {
			if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
				return { ok: false, message: "Expected YYYY-MM-DD" };
			}
			const d = new Date(`${trimmed}T00:00:00`);
			if (Number.isNaN(d.getTime())) {
				return { ok: false, message: "Invalid date" };
			}
			return { ok: true, normalized: trimmed };
		}
		case "time": {
			const m = trimmed.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
			if (!m) {
				return { ok: false, message: "Expected HH:mm or HH:mm:ss" };
			}
			const hh = Number(m[1]);
			const mm = Number(m[2]);
			if (hh > 23 || mm > 59) {
				return { ok: false, message: "Invalid time" };
			}
			return { ok: true, normalized: `${m[1]}:${m[2]}` };
		}
		default:
			return { ok: true, normalized: trimmed };
	}
}

export function buildEntriesFromCsv(csvText: string, dataType: VariableDataType): CsvImportResult {
	const parsedResult = parseCsvFirstColumnValues(csvText);
	if ("error" in parsedResult) {
		return {
			ok: false,
			errors: [{
				line: parsedResult.line ?? 0,
				value: "",
				message: parsedResult.error,
			}],
		};
	}

	const parsed = parsedResult;
	if (parsed.length === 0) {
		return {
			ok: false,
			errors: [{ line: 0, value: "", message: "No entries found in CSV" }],
		};
	}

	const errors: CsvImportLineError[] = [];
	const normalizedValues: string[] = [];

	for (const { line, value } of parsed) {
		const result = validateEntryValueForDataType(value, dataType);
		if (!result.ok) {
			errors.push({ line, value, message: result.message });
		} else {
			normalizedValues.push(result.normalized);
		}
	}

	if (errors.length > 0) {
		return { ok: false, errors };
	}

	const entries: CollectionEntry[] = normalizedValues.map((value) => createDraftCollectionEntry(value));

	return { ok: true, entries };
}
